import { useEffect, useRef, useState } from "react";
import { readCache, readCacheEntry, subscribe, fetchAndCache } from "./dataCache";

// If a cache entry is fresher than STALE_MS, skip the background revalidation.
// Keeps UI instant AND avoids useless re-fetching while the user bounces
// between pages. Mutations always invalidate via the axios interceptor.
const STALE_MS = 30_000;

export function useCachedData(key, fetcher, { enabled = true, staleMs = STALE_MS } = {}) {
  const [data, setData] = useState(() => readCache(key));
  const [loading, setLoading] = useState(() => readCache(key) === undefined && enabled);
  const [error, setError] = useState(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const mountedRef = useRef(true);

  const run = (force = false) => {
    if (!enabled) return;
    // Skip background revalidation if we have fresh data.
    if (!force) {
      const entry = readCacheEntry(key);
      if (entry && entry.ts && Date.now() - entry.ts < staleMs) {
        return;
      }
    }
    if (readCache(key) === undefined) setLoading(true);
    setError(null);
    fetchAndCache(key, () => fetcherRef.current(), { force })
      .catch((e) => { if (mountedRef.current) setError(e); })
      .finally(() => { if (mountedRef.current) setLoading(false); });
  };

  useEffect(() => {
    mountedRef.current = true;
    const unsub = subscribe(key, (next) => { if (mountedRef.current) setData(next); });
    run(false);
    return () => { mountedRef.current = false; unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return { data, loading, error, reload: () => run(true) };
}
